import React, { FC, useCallback, useContext, useEffect, useState } from "react";
import { Input, Typography } from "antd";
import { SEARCH } from "../i18n/en";
import { Styles, View } from "@data-root/types";
import { useQuery } from "@tanstack/react-query";
import { useAppContext, useClientContext, useNavigation } from "@data-root/react";

interface HeaderCenterProps {
  view?: View;
  styles: Styles;
  onQuery: (value: string) => void;
}

const { Text } = Typography;

export const HeaderCenter: FC<HeaderCenterProps> = ({
  view,
  styles,
  onQuery,
}) => {
  const { navigation } = useNavigation();

  const { core } = useClientContext();
  const { appSpecifier } = useAppContext();

  const rowId = navigation.getParam("rowId");

  const [defaultQuery] = useState(navigation.getParam("query"));
  const [query, setQuery] = useState<string | undefined>(defaultQuery);

  const { data: title } = useQuery({
    queryKey: ["title", appSpecifier, view, rowId],
    queryFn: async () => {
      if (!view) return "";
      if (!view.displayName) return view.name;

      const row = rowId
        ? await core.get(appSpecifier, view.tableId, rowId)
        : undefined;

      const result = await core.evaluateFormula<string>(view.displayName, {
        appId: appSpecifier.appId,
        deploymentId: appSpecifier.deploymentId,
        tableId: view.tableId,
        rowId: rowId,
        row,
      });

      return result;
    },
    placeholderData: view?.name ?? "",
  });

  const onClearQuery = useCallback(() => {
    setQuery("");
  }, []);

  useEffect(() => {
    core.emitter.on("clearQuery", onClearQuery);

    return () => {
      core.emitter.off("clearQuery", onClearQuery);
    };
  }, [core.emitter, onClearQuery]);

  useEffect(() => {
    onClearQuery();
  }, [view, onClearQuery]);

  return view && "showTitle" in view && view.showTitle ? (
    <Text
      strong
      style={{
        color: styles.lightTintColor,
      }}
    >
      {title}
    </Text>
  ) : (
    <Input.Search
      id="app-search-input"
      key={"search" + view?.name}
      placeholder={SEARCH}
      onSearch={onQuery}
      value={query}
      onChange={(e) => setQuery(e.target.value)}
    />
  );
};
